import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { api } from '../../api';

/* Context */
import { useAuthContext } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';

/* Components */
import ConfirmationModal from '../cards/ConfirmationModal';
import InviteModal from './InviteModal';

/* CSS */
import styles from './ShareModal.module.css';
import shared from '../../styles/shared.module.css';
import shareStyles from './share.module.css';

/* Icons */
import select_chevron from '../../assets/icons/select_chevron.svg';
import tick from '../../assets/icons/tick_icon_filled_blue.png';

import access_anyone from '../../assets/icons/access_anyone.svg';
import access_restricted from '../../assets/icons/access_restricted.svg';
import access_link_clicked from '../../assets/icons/access_link_clicked.svg';
import access_link_unclicked from '../../assets/icons/access_link_unclicked.svg';
import default_avatar from '../../assets/icons/default_avatar.svg';

const ACCESS_LEVEL_INFO = {
    restricted: { title: 'Restricted', subtitle: 'Only people with access can open the link' },
    anyone: { title: 'Anyone with the link', subtitle: 'Anyone with the link can open the trip' }
};
const ROLE_OPTIONS = [
    { value: 'editor', label: 'Editor' },
    { value: 'viewer', label: 'Viewer' },
];
const ACCESS_OPTIONS = [
    { value: 'restricted', label: 'Restricted' },
    { value: 'anyone', label: 'Anyone with the link' }
];

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const ShareModal = ({ isOpen, onClose, tripId, tripTitle, access }) => {

    const navigate = useNavigate();

    const { currentUser } = useAuthContext();
    const { showToast } = useToast();

    // Member list
    const [members, setMembers] = useState([]);
    // Invite link (general access)
    const [invite, setInvite] = useState(null);
    // Store the memberId being removed/transferred (or null when closed)
    const [confirmSelfRemove, setConfirmSelfRemove] = useState(null);
    const [confirmTransfer, setConfirmTransfer] = useState(null);

    const [loading, setLoading] = useState(false);
    const [hoverCopyLink, setHoverCopyLink] = useState(false);
    const [openMenuId, setOpenMenuId] = useState(null);

    // Invite section
    const [inviteEmail, setInviteEmail] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [showSearchDropdown, setShowSearchDropdown] = useState(false);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [initialInvitee, setInitialInvitee] = useState(null);
    // Which suggestion is highlighted for keyboard nav (-1 = none)
    const [activeIndex, setActiveIndex] = useState(-1);

    // Determine if user can edit roles
    const canEditRoles = access.isMember && (access.role === 'owner' || access.role === 'editor');
    const isOwner = access.role === 'owner';
    const dropdownRef = useRef(null);
    const searchRef = useRef(null);
    const searchTimerRef = useRef(null);

    // Change the role of a member
    const handleRoleChange = async (memberId, newRole) => {
        try {
            if (newRole === 'remove') {
                // Check for self-removal logic
                if (memberId === members.find(m => m.user_id === currentUser.id)?.member_id) {
                    setConfirmSelfRemove(memberId);
                    setOpenMenuId(null);
                    return;
                }
                // Normal remove access
                await api.delete(`/share/${tripId}/members/${memberId}`);
                setMembers(prev => prev.filter(m => m.member_id !== memberId));
            } else if (newRole === 'transfer') {
                setConfirmTransfer(memberId);
                setOpenMenuId(null);
                return;
            } else {
                await api.patch(`/share/${tripId}/members/${memberId}`, { role: newRole });
                setMembers(prev => prev.map(x => x.member_id === memberId ? { ...x, role: newRole } : x));
                showToast('Member role updated', 'success');
            }
            setOpenMenuId(null);
        } catch (err) {
            showToast(err.message || 'Failed to update member', 'error');
        }
    };

    // Change the access level (restriced/anyone) of the general access setting
    const handleAccessLevelChange = async (newLevel) => {
        try {
            await api.post(`/share/${tripId}/invite-link`, {
                access: newLevel,
                accessRole: invite?.default_role || 'viewer'
            });
            // Update local state
            setInvite({
                ...invite,
                access_level: newLevel
            });
            setOpenMenuId(null);
            showToast('General access level updated', 'success');
        } catch (err) {
            showToast(err.message || 'Failed to change access level', 'error');
        }
    };

    // Change the default role (editor/viewer) of the general access setting
    const handleDefaultRoleChange = async (newRole) => {
        try {
            await api.post(`/share/${tripId}/invite-link`, {
                access: invite?.access_level || 'restricted',
                accessRole: newRole
            });
            // Update local state
            setInvite({
                ...invite,
                default_role: newRole
            });
            setOpenMenuId(null);
            showToast('Default role updated', 'success');
        } catch (err) {
            showToast(err.message || 'Failed to change default role', 'error');
        }
    };

    // Remove self from trip
    const handleConfirmSelfRemove = async () => {
        try {
            await api.delete(`/share/${tripId}/members/${confirmSelfRemove}`);
            showToast("Successfully removed yourself from trip", "success");
            navigate(`/trips`);
            onClose();
        } catch (err) {
            showToast(err.message || 'Failed to remove yourself from trip', 'error');
        }
    };

    // Transfer trip ownership
    const handleConfirmTransfer = async () => {
        try {
            await api.patch(`/share/${tripId}/transfer-ownership`, { newOwnerId: confirmTransfer });
            // Promote new owner and demote current owner for local states
            setMembers(prev => prev.map(m => {
                if (m.member_id === confirmTransfer) return { ...m, role: 'owner' };
                if (m.user_id === currentUser.id) return { ...m, role: 'editor' };
                return m;
            }));
            setConfirmTransfer(null);
            showToast('Successfully transferred ownership', 'success');
        } catch (err) {
            showToast(err.message || 'Failed to transfer ownership', 'error');
        }
    };

    // Search users as the invite input changes (debounced)
    const handleInviteInputChange = (e) => {
        const value = e.target.value;
        setInviteEmail(value);

        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);

        if (!value.trim()) {
            setSearchResults([]);
            setShowSearchDropdown(false);
            setActiveIndex(-1);
            return;
        }

        searchTimerRef.current = setTimeout(async () => {
            try {
                const results = await api.get(`/share/users/search?q=${encodeURIComponent(value.trim())}`);
                setSearchResults(results);
                setShowSearchDropdown(results.length > 0);
                setActiveIndex(-1);
            } catch (err) {
                // Surface the rate-limit case so the user knows why suggestions stopped
                if (err.status === 429) showToast(err.message, 'error');
            }
        }, 300);
    };

    // Keyboard navigation for the suggestion dropdown
    const handleInviteKeyDown = (e) => {
        const hasSuggestions = showSearchDropdown && searchResults.length > 0;

        if (hasSuggestions && e.key === 'ArrowDown') {
            e.preventDefault(); // don't jump the text caret
            setActiveIndex(i => Math.min(i + 1, searchResults.length - 1));
            return;
        }
        if (hasSuggestions && e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex(i => Math.max(i - 1, 0));
            return;
        }
        if (e.key === 'Enter') {
            // A highlighted suggestion wins over the raw text
            if (hasSuggestions && activeIndex >= 0) {
                handleOpenInviteModal(searchResults[activeIndex]);
                return;
            }
            // Otherwise treat the typed text as an email to invite
            const email = inviteEmail.trim();
            if (!email) return;
            if (isValidEmail(email)) {
                handleOpenInviteModal(email);
            } else {
                showToast('Please enter a valid email address', 'error');
            }
        }
    };

    // Open the InviteModal, pre-seeded with the selected user or typed email
    const handleOpenInviteModal = (userOrEmail) => {
        const invitee = typeof userOrEmail === 'string'
            ? { email: userOrEmail }
            : { email: userOrEmail.email, username: userOrEmail.username, profile_picture_url: userOrEmail.profile_picture_url };
        setInitialInvitee(invitee);
        setInviteEmail('');
        setSearchResults([]);
        setShowSearchDropdown(false);
        setActiveIndex(-1);
        setShowInviteModal(true);
    };

    // Copy invite link to clipboard
    const handleCopyLink = async () => {
        if (!invite?.invite_token) return;

        const joinUrl = `${window.location.origin}/join/${invite.invite_token}`;
        try {
            await navigator.clipboard.writeText(joinUrl);
            showToast('Link copied to clipboard', 'success');
        } catch (err) {
            showToast('Failed to copy link', 'error');
        }
    };

    // Toggle menu for dropdowns
    const handleToggleMenu = (memberId) => {
        setOpenMenuId(prev => prev === memberId ? null : memberId);
    };

    // Load share data from backend
    useEffect(() => {
        if (!isOpen) return;

        async function loadShareData() {
            setLoading(true);
            try {
                const res = await api.get(`/share/${tripId}/members`);
                setMembers(res.members || []);
                setInvite(res.invite || null);
            } catch (err) {
                showToast(err.message || 'Failed to load share data', 'error');
            } finally {
                setLoading(false);
            }
        }

        loadShareData();
    }, [isOpen, tripId]);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setOpenMenuId(null);
            }
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setShowSearchDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [dropdownRef]);

    // The early-exit guard
    if (!isOpen) return null;

    // Hand off to InviteModal when open
    if (showInviteModal) {
        return createPortal(
            <InviteModal
                onBack={() => setShowInviteModal(false)}
                onDismiss={() => { setShowInviteModal(false); onClose(); }}
                tripId={tripId}
                initialInvitee={initialInvitee}
                invite={invite}
            />,
            document.body
        );
    }

    return createPortal(
        <div className={shared.modalOverlay}>
            <div className={shared.modalContent}>
                {/* Title */}
                <div className={styles.modalTitle}>
                    Share: <strong>"{tripTitle}"</strong>
                </div>

                {/* Invite by email — opens InviteModal */}
                {canEditRoles && (
                    <div className={styles.inviteSection} ref={searchRef}>
                        <div className={styles.inviteInputWrapper}>
                            <input
                                type="text"
                                className={styles.inviteInput}
                                placeholder="Add people by email"
                                value={inviteEmail}
                                onChange={handleInviteInputChange}
                                onKeyDown={handleInviteKeyDown}
                            />
                            {showSearchDropdown && searchResults.length > 0 && (
                                <div className={shareStyles.searchDropdown}>
                                    {searchResults.map((user, index) => (
                                        <div
                                            key={user.id}
                                            className={`${shareStyles.searchResult} ${activeIndex === index ? shareStyles.searchResultActive : ''}`}
                                            onMouseEnter={() => setActiveIndex(index)}
                                            onMouseDown={(e) => {
                                                e.preventDefault();
                                                handleOpenInviteModal(user);
                                            }}
                                        >
                                            <img
                                                src={user.profile_picture_url || default_avatar}
                                                alt={user.username}
                                                className={shareStyles.searchResultAvatar}
                                            />
                                            <div className={shareStyles.searchResultInfo}>
                                                <span className={shareStyles.searchResultName}>{user.username}</span>
                                                <span className={shareStyles.searchResultEmail}>{user.email}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* People with access */}
                {access.isMember && (
                    <div className={styles.section}>
                        <div className={styles.sectionTitle}>People with access</div>
                        {loading ? (
                            <div className={shared.spinner} />
                        ) : (
                            <ul>
                                {/* Spreading it into a new array [...members] */}
                                {[...members]
                                    .sort((a, b) => {
                                        const order = { owner: 0, editor: 1, viewer: 2 };
                                        return (order[a.role] ?? 3) - (order[b.role] ?? 3);
                                    })
                                    .map(member => {
                                        // Disable role editing for when targeting the owner
                                        const isTargetOwner = member.role === 'owner';

                                        return (
                                            <li key={member.member_id} className={styles.memberRow}>
                                                <div className={styles.memberInfo}>
                                                    <img
                                                        src={member.profile_picture_url || default_avatar}
                                                        alt={member.display_name}
                                                        className={shareStyles.personAvatar}
                                                    />
                                                    <div className={shareStyles.personInfo}>
                                                        <span className={styles.memberName}>{member.display_name} {member.user_id === currentUser.id && '(You)'}</span>
                                                        <span className={styles.memberEmail}>{member.email}</span>
                                                    </div>
                                                </div>

                                                {(canEditRoles && !isTargetOwner) ? (
                                                    <div
                                                        className={shareStyles.roleButton}
                                                        style={{ marginLeft: '-4px' }}
                                                        onClick={() => handleToggleMenu(member.member_id)}
                                                    >
                                                        {/* Member role */}
                                                        <span className={shareStyles.roleButtonTitle}>
                                                            {member.role === 'editor' ? 'Editor' : 'Viewer'}
                                                        </span>
                                                        <img src={select_chevron} alt="Choose member role" className={shareStyles.roleButtonChevron} />

                                                        {/* Dropdown menu */}
                                                        {openMenuId === member.member_id && (
                                                            <div className={shareStyles.roleDropdown} ref={dropdownRef}>

                                                                {/* Viewer/editor */}
                                                                {ROLE_OPTIONS.map((option) => (
                                                                    <div
                                                                        key={option.value}
                                                                        className={shareStyles.roleDropdownItem}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleRoleChange(member.member_id, option.value)
                                                                        }}
                                                                    >
                                                                        <div className={shareStyles.tickSlot}>
                                                                            {option.value === member.role && (
                                                                                <img src={tick} alt="Selected" className={shareStyles.tickIcon} />
                                                                            )}
                                                                        </div>
                                                                        <span>{option.label}</span>
                                                                        <div className={shareStyles.tickSlot} />
                                                                    </div>
                                                                ))}
                                                                <div className={shareStyles.separator} />

                                                                {/* Transfer ownership */}
                                                                {isOwner && (
                                                                    <div
                                                                        className={shareStyles.roleDropdownItem}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleRoleChange(member.member_id, 'transfer')
                                                                        }}
                                                                    >
                                                                        <div className={shareStyles.tickSlot} />
                                                                        <span>Transfer ownership</span>
                                                                        <div className={shareStyles.tickSlot} />
                                                                    </div>
                                                                )}

                                                                {/* Remove access */}
                                                                <div
                                                                    className={shareStyles.roleDropdownItem}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleRoleChange(member.member_id, 'remove')
                                                                    }}
                                                                >
                                                                    <div className={shareStyles.tickSlot} />
                                                                    <span>Remove access</span>
                                                                    <div className={shareStyles.tickSlot} />
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className={`${styles.memberRole} ${member.role === 'owner' ? styles.ownerLabel : member.role === 'viewer' ? styles.viewerLabel : ''}`}>
                                                        {member.role}
                                                    </span>
                                                )}
                                            </li>
                                        )
                                    })
                                }
                            </ul>
                        )}
                    </div>
                )}

                {/* General access */}
                <div className={styles.section}>
                    <div className={styles.sectionTitle}>General access</div>

                    <div className={styles.generalAccess}>
                        <div className={styles.generalAccessLevel}>
                            {/* The lock */}
                            <div className={styles.accessIconWrapper}>
                                <img
                                    src={invite?.access_level === 'anyone' ? access_anyone : access_restricted}
                                    alt={invite?.access_level === 'anyone' ? 'Anyone can join' : 'Restricted access'}
                                    className={styles.accessIcon}
                                />
                            </div>
                            {/* Restricted/anyone */}
                            <div className={styles.generalAccessDetails}>
                                <div
                                    className={shareStyles.roleButton}
                                    style={{ marginLeft: '-4px' }}
                                    onClick={canEditRoles ? () => handleToggleMenu('generalAccess') : undefined}
                                >
                                    {/* Restricted/anyone text */}
                                    <span className={shareStyles.roleButtonTitle}>
                                        {ACCESS_LEVEL_INFO[invite?.access_level || 'restricted'].title}
                                    </span>
                                    {/* Chevron */}
                                    {canEditRoles && (
                                        <img src={select_chevron} alt="Choose general access mode" className={shareStyles.roleButtonChevron} />
                                    )}

                                    {/* Dropdown */}
                                    {canEditRoles && openMenuId === 'generalAccess' && (
                                        <div className={shareStyles.roleDropdown} ref={dropdownRef}>
                                            {ACCESS_OPTIONS.map((option) => (
                                                <div
                                                    key={option.value}
                                                    className={shareStyles.roleDropdownItem}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleAccessLevelChange(option.value);
                                                    }}
                                                >
                                                    <div className={shareStyles.tickSlot}>
                                                        {option.value === (invite?.access_level || 'restricted') && (
                                                            <img src={tick} alt="Selected" className={shareStyles.tickIcon} />
                                                        )}
                                                    </div>
                                                    <span>{option.label}</span>
                                                    <div className={shareStyles.tickSlot} />
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <span className={styles.generalAccessSubtitle}>{ACCESS_LEVEL_INFO[invite?.access_level || 'restricted'].subtitle}</span>
                            </div>
                        </div>

                        {/* Default role for "anyone with the link" */}
                        {invite?.access_level === 'anyone' && (
                            <div
                                className={shareStyles.roleButton}
                                style={{ marginLeft: '-4px' }}
                                onClick={canEditRoles ? () => handleToggleMenu('defaultRole') : undefined}
                            >
                                {/* Role text */}
                                <span className={shareStyles.roleButtonTitle}>
                                    {invite?.default_role === 'editor' ? 'Editor' : 'Viewer'}
                                </span>
                                {/* Chevron */}
                                {canEditRoles && (
                                    <img src={select_chevron} alt="Choose general access role" className={shareStyles.roleButtonChevron} />
                                )}
                                {/* Dropdown */}
                                {canEditRoles && openMenuId === 'defaultRole' && (
                                    <div className={shareStyles.roleDropdown} ref={dropdownRef}>
                                        {ROLE_OPTIONS.map((option) => (
                                            <div
                                                key={option.value}
                                                className={shareStyles.roleDropdownItem}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDefaultRoleChange(option.value);
                                                }}
                                            >
                                                <div className={shareStyles.tickSlot}>
                                                    {option.value === (invite?.default_role || 'viewer') && (
                                                        <img src={tick} alt="Selected" className={shareStyles.tickIcon} />
                                                    )}
                                                </div>
                                                <span>{option.label}</span>
                                                <div className={shareStyles.tickSlot} />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Action buttons */}
                <div className={shareStyles.actionButtons}>
                    <button
                        className={`${shareStyles.actionButton} ${styles.copyLink}`}
                        onClick={handleCopyLink}
                        onMouseEnter={() => setHoverCopyLink(true)}
                        onMouseLeave={() => setHoverCopyLink(false)}
                    >
                        <img
                            src={hoverCopyLink ? access_link_clicked : access_link_unclicked}
                            alt="Copy link"
                            className={styles.accessLink}
                        />
                        <span>Copy link</span>
                    </button>
                    <button className={`${shareStyles.actionButton} ${styles.close}`} onClick={onClose}>
                        <span>Done</span>
                    </button>
                </div>
            </div>

            <ConfirmationModal
                isOpen={!!confirmSelfRemove}
                title="Remove yourself from trip?"
                message="You will not be able to access this trip"
                confirmText="Remove access"
                isDanger={true}
                onConfirm={handleConfirmSelfRemove}
                onClose={() => setConfirmSelfRemove(null)}
            />

            <ConfirmationModal
                isOpen={!!confirmTransfer}
                title="Transfer ownership?"
                message="You will become an editor and lose owner privileges. This cannot be undone."
                confirmText="Transfer"
                isDanger={true}
                onConfirm={handleConfirmTransfer}
                onClose={() => setConfirmTransfer(null)}
            />
        </div>,
        document.body
    );
};

export default ShareModal;